import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/AppLayout';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import ChangePassword from './pages/auth/ChangePassword';
import PhotographerSignup from './pages/auth/PhotographerSignup';
import DealerSignup from './pages/auth/DealerSignup';
import ForgotPassword from './pages/auth/ForgotPassword';

// Public pages
import PackagesPage from './pages/public/PackagesPage';

// Visitor pages
import RegisterPage from './pages/visitor/RegisterPage';
import GalleryPage from './pages/visitor/GalleryPage';

// Admin pages
import AdminEvents from './pages/admin/AdminEvents';
import AdminEventDetail from './pages/admin/AdminEventDetail';
import AdminMatchingPage from './pages/admin/AdminMatchingPage';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import SuperAdminEventDetail from './pages/admin/SuperAdminEventDetail';
import SuperAdminPhotographers from './pages/admin/SuperAdminPhotographers';
import SuperAdminLiveDashboard from './pages/admin/SuperAdminLiveDashboard';
import SuperAdminDealers from './pages/admin/SuperAdminDealers';
import SuperAdminPackages from './pages/admin/SuperAdminPackages';
import EventPhotosViewer from './pages/admin/EventPhotosViewer';
import SuperAdminInvestigate from './pages/admin/SuperAdminInvestigate';
import SuperAdminUsersManagement from './pages/admin/SuperAdminUsersManagement';
import SuperAdminDealersPage from './pages/admin/SuperAdminDealersPage';
import SuperAdminPhotographersPage from './pages/admin/SuperAdminPhotographersPage';
import SuperAdminEventOwnersPage from './pages/admin/SuperAdminEventOwnersPage';
import SuperAdminVisitorsMgmtPage from './pages/admin/SuperAdminVisitorsMgmtPage';

// Dealer pages
import DealerDashboard from './pages/dealer/DealerDashboard';
import DealerEventDetail from './pages/dealer/DealerEventDetail';

// ── Route guards ──────────────────────────────────────────────────────────────

// Wraps route in AppLayout + auth check
const Protected = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
};

// Super admin only
const SuperAdminOnly = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'super_admin') return <Navigate to="/admin/events" replace />;
  return <AppLayout>{children}</AppLayout>;
};

// Dealer only
const DealerOnly = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'dealer') return <Navigate to="/dealer/dashboard" replace />;
  return <AppLayout>{children}</AppLayout>;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter basename="/" future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          {/* Public / unauthenticated */}
          <Route path="/login"           element={<LoginPage />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/signup"          element={<PhotographerSignup />} />
          <Route path="/dealer/signup"   element={<DealerSignup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/packages"        element={<PackagesPage />} />

          {/* Visitor */}
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/gallery"  element={<GalleryPage />} />

          {/* Photographer / Admin (any authenticated user) */}
          <Route path="/admin/login"          element={<Navigate to="/login" replace />} />
          <Route path="/admin/events"          element={<Protected><AdminEvents /></Protected>} />
          <Route path="/admin/events/:id"      element={<Protected><AdminEventDetail /></Protected>} />
          <Route path="/admin/events/:id/match"   element={<Protected><AdminMatchingPage /></Protected>} />
          <Route path="/admin/events/:id/browser" element={<Protected><EventPhotosViewer /></Protected>} />

          {/* Super Admin */}
          <Route path="/admin/super"                    element={<SuperAdminOnly><SuperAdminDashboard /></SuperAdminOnly>} />
          <Route path="/admin/super/events/:id"         element={<SuperAdminOnly><SuperAdminEventDetail /></SuperAdminOnly>} />
          {/* photographers route replaced by SuperAdminPhotographersPage below */}
          <Route path="/admin/super/live"               element={<SuperAdminOnly><SuperAdminLiveDashboard /></SuperAdminOnly>} />
          <Route path="/admin/super/dealers"            element={<SuperAdminOnly><SuperAdminDealers /></SuperAdminOnly>} />
          <Route path="/admin/super/packages"           element={<SuperAdminOnly><SuperAdminPackages /></SuperAdminOnly>} />
          <Route path="/admin/super/investigate"        element={<SuperAdminOnly><SuperAdminInvestigate /></SuperAdminOnly>} />
          <Route path="/admin/super/users"              element={<SuperAdminOnly><SuperAdminUsersManagement /></SuperAdminOnly>} />
          <Route path="/admin/super/photographers"      element={<SuperAdminOnly><SuperAdminPhotographersPage /></SuperAdminOnly>} />
          <Route path="/admin/super/event-owners"       element={<SuperAdminOnly><SuperAdminEventOwnersPage /></SuperAdminOnly>} />
          <Route path="/admin/super/visitors"           element={<SuperAdminOnly><SuperAdminVisitorsMgmtPage /></SuperAdminOnly>} />

          {/* Dealer */}
          <Route path="/dealer/dashboard"  element={<DealerOnly><DealerDashboard /></DealerOnly>} />
          <Route path="/dealer/events/:id" element={<DealerOnly><DealerEventDetail /></DealerOnly>} />

          {/* Default redirects */}
          <Route path="/admin" element={<Navigate to="/admin/events" replace />} />
          <Route path="/"      element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
